import { Fragment, useMemo, useState } from "react";
import { ChevronRight, Lock } from "lucide-react";
import { useTranslation } from "../../i18n";
import { positionLabelKey, type Position } from "../../access/positions";
import AccessLevelCell from "./AccessLevelCell";
import { AccessRules, LevelOrInherit, cycleLevel, effectiveLevel, storedLevel, isGated, areaMenuKey } from "./acHelpers";
import { AccessControlConfig } from "./types";

// "Default" is not a real warehouse name, so it gets a sentinel scope id
// that can't collide with one.
const DEFAULT_SCOPE = "__default__";

const groupLabel = (t: (key: string) => string, id: string, apiLabel: string): string => {
    const key = `ac_group_${id}`;
    const translated = t(key);
    return translated === key ? apiLabel : translated;
};

export interface MatrixViewProps {
    config: AccessControlConfig;
    warehouses: string[];
    /** Rules with any in-progress edits already overlaid (see applyEdits). */
    rules: AccessRules;
    onCycle: (subjectId: string, warehouse: string | null, key: string, next: LevelOrInherit) => void;
}

const MatrixView = ({ config, warehouses, rules, onCycle }: MatrixViewProps) => {
    const { t } = useTranslation();
    const [scope, setScope] = useState<string>(DEFAULT_SCOPE);
    const [filter, setFilter] = useState("");
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const warehouse = scope === DEFAULT_SCOPE ? null : scope;

    const filteredKeys = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return config.keys;
        return config.keys.filter(k => k.label.toLowerCase().includes(q) || k.key.toLowerCase().includes(q));
    }, [config.keys, filter]);

    const byGroup = useMemo(() => {
        const map = new Map<string, typeof config.keys>();
        filteredKeys.forEach(k => {
            if (!map.has(k.group)) map.set(k.group, []);
            map.get(k.group)!.push(k);
        });
        return map;
    }, [filteredKeys]);

    return (
        <div className="ac-matrix">
            <div className="ac-matrix-toolbar">
                <div className="ac-scope-tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === DEFAULT_SCOPE}
                        className={`ac-scope-tab${scope === DEFAULT_SCOPE ? " ac-scope-tab--active" : ""}`}
                        onClick={() => setScope(DEFAULT_SCOPE)}
                    >
                        {t("ac_defaults_tab")}
                    </button>
                    {warehouses.map(w => (
                        <button
                            key={w}
                            type="button"
                            role="tab"
                            aria-selected={scope === w}
                            className={`ac-scope-tab${scope === w ? " ac-scope-tab--active" : ""}`}
                            onClick={() => setScope(w)}
                        >
                            {w}
                        </button>
                    ))}
                </div>
                <input
                    className="ac-filter-input"
                    type="text"
                    placeholder={t("ac_filter_placeholder")}
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    aria-label={t("ac_filter_placeholder")}
                />
            </div>

            <div className="ac-matrix-scroll">
                <table className="ac-matrix-table">
                    <thead>
                        <tr>
                            <th className="ac-matrix-key-col">{t("ac_key_col")}</th>
                            {config.positions.map(p => (
                                <th key={p} className={p === "Admin" ? "ac-matrix-admin-col" : undefined}>
                                    {t(positionLabelKey(p as Position))}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {config.groups.map(g => {
                            const groupKeys = byGroup.get(g.id) ?? [];
                            if (groupKeys.length === 0) return null;
                            const isCollapsed = !!collapsed[g.id];
                            return (
                                <Fragment key={g.id}>
                                    <tr className="ac-matrix-group-row">
                                        <td colSpan={config.positions.length + 1}>
                                            <button
                                                type="button"
                                                className="ac-matrix-group-toggle"
                                                onClick={() => setCollapsed(c => ({ ...c, [g.id]: !c[g.id] }))}
                                            >
                                                <ChevronRight
                                                    size={14}
                                                    className={`ac-matrix-group-chevron${isCollapsed ? "" : " ac-matrix-group-chevron--open"}`}
                                                />
                                                <span>{groupLabel(t, g.id, g.label)}</span>
                                                <span className="ac-matrix-group-count">{groupKeys.length}</span>
                                            </button>
                                        </td>
                                    </tr>
                                    {!isCollapsed && groupKeys.map(k => (
                                        <tr key={k.key} className="ac-matrix-key-row">
                                            <td className="ac-matrix-key-col">
                                                <div className="ac-key-label">
                                                    <span>{k.label}</span>
                                                    {k.kind === "menu" && <span className="ac-tag ac-tag--menu">{t("ac_menu_tag")}</span>}
                                                    {k.floor && (
                                                        <span
                                                            className="ac-floor-lock"
                                                            title={t("ac_floor_note", { floor: k.floor })}
                                                        >
                                                            <Lock size={12} aria-hidden="true" />
                                                        </span>
                                                    )}
                                                </div>
                                                <code className="ac-key-code">{k.key}</code>
                                            </td>
                                            {config.positions.map(p => {
                                                if (p === "Admin") {
                                                    return (
                                                        <td key={p} className="ac-matrix-admin-col">
                                                            <AccessLevelCell
                                                                stored="write"
                                                                effective="write"
                                                                locked
                                                                title={t("ac_admin_locked")}
                                                                onCycle={() => {}}
                                                                ariaLabel={`${k.label} Admin`}
                                                            />
                                                        </td>
                                                    );
                                                }
                                                const stored = storedLevel({ rules, subjectType: "position", subjectId: p, warehouse, key: k.key });
                                                const eff = effectiveLevel({ keyDef: k, rules, position: p, warehouse });
                                                const gated = isGated(k.key, config.keys, { rules, position: p, warehouse });
                                                const inheritTitle = stored === "inherit"
                                                    ? t(warehouse ? "ac_inherited_from_default" : "ac_inherited_from_code_default")
                                                    : undefined;
                                                const title = gated
                                                    ? [t("ac_gated_by_area", { area: t(areaMenuKey(k.key) === "admin" ? "admin" : "app") }), inheritTitle].filter(Boolean).join(" · ")
                                                    : inheritTitle;
                                                return (
                                                    <td key={p}>
                                                        <AccessLevelCell
                                                            stored={stored}
                                                            effective={eff.level}
                                                            gated={gated}
                                                            title={title}
                                                            onCycle={() => onCycle(p, warehouse, k.key, cycleLevel(stored))}
                                                            ariaLabel={`${k.label} ${t(positionLabelKey(p as Position))}`}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MatrixView;
